import { useLoad } from "@allergy123/api-client/react";
import { Button, Card, Status } from "@allergy123/ui";
import { Link } from "react-router";
import { enrollmentFor, isComplete, stageLabel } from "../lib";

/** The catalog: levels, sections, modules, classes — with the learner's own status alongside. */
export function TrainingPage() {
  const data = useLoad(async (api) => {
    const [cat, learning] = await Promise.all([api.catalog(), api.learning.me()]);
    return { cat, learning };
  });
  if (data.state === "error") return <Status kind="error" label={`Couldn't load the catalog (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { cat, learning } = data.data;

  return (
    <>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">Training</h1>
      <p className="mb-2 max-w-[68ch] text-ink-secondary">
        One shared hierarchy: level, section, module, class. Level 2 sections are the role pathways; take only the ones for the work you actually do.
      </p>
      <p className="mb-10 text-small text-ink-secondary">
        Curriculum map v{cat.version} · source {cat.source.status.replace("-", " ")} · classes marked "outline only" have no media yet.
      </p>

      <Card tone="tinted" className="mb-10" as="section">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Status kind="warning" label="Draft practice tool" className="mb-3" />
            <h2 className="text-h3 font-semibold text-anchor">Skin testing proficiency calculator</h2>
            <p className="mt-2 max-w-[62ch] text-ink-secondary">
              Calculate the control-response portion of the 10 histamine and 10 matched-negative-control exercise. The evaluator's procedural rubric remains separate.
            </p>
          </div>
          <Button href="/training/proficiency/skin-testing" variant="secondary" surface="card-tinted">
            Open calculator
          </Button>
        </div>
      </Card>

      {cat.levels.map((level) => {
        const sections = cat.sections.filter((s) => s.levelNumber === level.number);
        if (!sections.length) return null;
        return (
          <section key={level.id} className="mb-12">
            <h2 className="mb-1 text-h2 font-semibold text-anchor">
              Level {level.number} · {level.title}
            </h2>
            <div className="mt-4 grid gap-6">
              {sections.map((s) => {
                const modules = cat.modules.filter((m) => m.sectionId === s.id).sort((a, b) => a.sequence - b.sequence);
                return (
                  <Card key={s.id} as="section">
                    <h3 className="text-h3 font-semibold text-anchor">{s.title}</h3>
                    {s.pathway && <p className="text-small text-ink-secondary">Role pathway</p>}
                    {modules.length === 0 ? (
                      <p className="mt-3 text-small text-ink-secondary">Governed workflow or assessment — not a class; nothing to complete here.</p>
                    ) : (
                      <ul className="mt-4 divide-y divide-rule-subtle">
                        {modules.map((m) => {
                          const e = enrollmentFor(learning.enrollments, m.code);
                          const done = m.classes.filter((k) => isComplete(e, k.code)).length;
                          return (
                            <li key={m.code} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-baseline sm:justify-between">
                              <div>
                                <span className="mr-3 text-small font-medium text-ink-secondary">{m.code}</span>
                                <Link to={`/training/${s.id}/${m.slug}`} className="font-medium">
                                  {m.title}
                                </Link>
                                <span className="ml-3 text-small text-ink-secondary">
                                  {m.classes.length} class{m.classes.length === 1 ? "" : "es"} · {stageLabel[m.releaseStage] ?? m.releaseStage}
                                </span>
                              </div>
                              <span className="text-small text-ink-secondary">{e ? `${done} of ${m.classes.length} complete` : "Not started"}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </Card>
                );
              })}
            </div>
          </section>
        );
      })}
    </>
  );
}
